import React, { useEffect, useMemo, useState } from 'react';
import type { Key } from 'react';
import { useParams } from 'react-router-dom';

import Tooltip from 'rc-tooltip';
import classNames from 'classnames';
import toast from 'react-hot-toast';

import { ColumnDef } from '@tanstack/react-table';

import Table from 'Components/Table';
import type { TableRowSelection } from 'Components/Table/types';

import { usePermissionContext } from 'contexts/PermissionContext';
import { Button, FlexBox, Text } from '@eltoro-ui/components';
import { Loading } from 'Components';
import AudienceStatusAccessor from 'Pages/SavedAudiences/AudienceStatusAccessor';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWalking } from '@fortawesome/pro-solid-svg-icons';

import { getAudiences } from 'Requests/Request_Methods/audienceMethods';

import type { OrderlineDetailsParams } from 'Pages/OrderlineDetails/index';

import type { TAudience, TResPagination } from 'types';
import { AudienceStatusEnum } from 'enums';

interface AudienceTableProps {
  selectedAudiences: TAudience[];
  alreadySelected: TAudience[];
  handleNewSelectedProspectCount: (newList: TAudience[]) => void;
  setSelectedAudiences: React.Dispatch<React.SetStateAction<TAudience[]>>;
}
const AudienceTable = ({
  selectedAudiences,
  alreadySelected,
  handleNewSelectedProspectCount,
  setSelectedAudiences,
}: AudienceTableProps) => {
  const { orderlineId } = useParams<OrderlineDetailsParams>();

  const [audiences, setAudiences] = useState<TAudience[]>([]);

  const { userIsActive } = usePermissionContext();

  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>(
    selectedAudiences.map(({ id }) => id)
  );

  const [page, setPage] = useState<number>(1);
  const [pagination, setPagination] = useState<TResPagination>({ total_count: 0, total_pages: 0 });

  const [fetchDataIsLoading, setFetchDataIsLoading] = useState(false);

  const _fetchData = async () => {
    setFetchDataIsLoading(true);
    try {
      const res = await getAudiences(page, 7, null, null, null, orderlineId);
      if (res.data?.audiences?.length) {
        setAudiences(prev => [...prev, ...res.data?.audiences]);
        const { total_audience, total_pages } = res.data;
        setPagination({ total_count: total_audience, total_pages });
      }
      setFetchDataIsLoading(false);
    } catch (err: any) {
      toast.error(err.detail);
      setFetchDataIsLoading(false);
    }
  };

  useEffect(() => {
    if (userIsActive) {
      _fetchData();
    }
  }, [page]);

  useEffect(() => {
    handleNewSelectedProspectCount(selectedAudiences);
  }, [selectedAudiences]);

  const isUsedAudienceSelected = (id: number) =>
    alreadySelected.some(audience => audience.id === id);

  const isTableRowDisable = (audience: TAudience) => {
    return (
      audience.status === AudienceStatusEnum.ERROR ||
      audience.status === AudienceStatusEnum.PENDING ||
      (audience.status === AudienceStatusEnum.IN_USE && !isUsedAudienceSelected(audience.id))
    );
  };

  const rowSelection = useMemo<TableRowSelection<TAudience>>(
    () => ({
      hideSelectAll: true,
      selectedRowKeys,
      onChange: (selectedRowKeys, selectedRows) => {
        setSelectedRowKeys(selectedRowKeys);
        setSelectedAudiences(prevTargets => {
          const filteredRows = prevTargets.filter(
            row => audiences.findIndex(({ id }) => id === row.id) === -1
          );
          return [...filteredRows, ...selectedRows];
        });
      },
      checkboxRender: (originalNode, row) => {
        if (isTableRowDisable(row) && row.status === AudienceStatusEnum.IN_USE) {
          const overlay = (
            <FlexBox flexDirection="column" gap="0.5rem">
              <span>
                This audience is already part of an active ad campaign, {row.campaign_name_in_use},
                and it can't be used in a new campaign.
              </span>
              <span>
                You can either edit the {row.campaign_name_in_use} campaign to add creatives or wait
                for it to finish before sending new ads.
              </span>
            </FlexBox>
          );
          return (
            <Tooltip
              placement="topLeft"
              trigger="hover"
              overlayClassName={classNames('audience-tooltip', 'audience-disable-in_use')}
              showArrow
              align={{
                offset: [-15, -3],
                targetOffset: [-6, 0],
              }}
              overlay={overlay}
            >
              <span>{originalNode}</span>
            </Tooltip>
          );
        }

        if (row.status === AudienceStatusEnum.PENDING)
          return (
            <Tooltip
              placement="topLeft"
              trigger="hover"
              overlayClassName={classNames('audience-tooltip', 'audience-disable-pending')}
              showArrow
              align={{
                offset: [-15, -3],
                targetOffset: [-6, 0],
              }}
              overlay="Your audience creation is in process. Please refresh after a few minutes."
            >
              <span>{originalNode}</span>
            </Tooltip>
          );

        return originalNode;
      },
    }),
    [selectedRowKeys, audiences]
  );

  const columns: ColumnDef<TAudience>[] = [
    {
      header: 'Name',
      accessorKey: 'name',
      enableSorting: false,
      accessorFn: audience => audience.name ?? '--',
    },
    {
      header: 'Status',
      accessorKey: 'status',
      enableSorting: false,
      enableResizing: false,
      size: 136,
      cell: ({ row: { original: audience } }) =>
        audience.status ? <AudienceStatusAccessor status={audience.status} /> : '-',
    },
    {
      header: 'Prospects',
      accessorKey: 'prospects_counts',
      enableSorting: false,
      cell: ({ row: { original: audience } }) => (
        <FlexBox alignItems="center" gap="10px">
          <Text on="white" size="l" weight="bold" UNSAFE_className="prospects_count">
            <FontAwesomeIcon
              icon={faWalking}
              color={
                audience.status !== AudienceStatusEnum.ACTIVE
                  ? '#D1D1D1'
                  : 'var(--color-secondary-300)'
              }
            />{' '}
            {audience.prospects_counts || '-'}
          </Text>
          <span className="prospect-info">PROSPECTS</span>
        </FlexBox>
      ),
    },
    {
      header: 'Source',
      accessorKey: 'source',
      enableSorting: false,
      accessorFn: audience => audience.source ?? '--',
    },
  ];

  return (
    <div className="SavedAudiences scrolable-table">
      <Text on="white" UNSAFE_style={{ display: 'block', padding: '16px' }}>
        {pagination.total_count} saved audience{pagination.total_count > 1 ? 's' : ''}
      </Text>
      {!!audiences.length && (
        <>
          <Table<TAudience>
            data={audiences}
            columns={columns}
            rowDisabled={audience => isTableRowDisable(audience)}
            rowSelection={rowSelection}
          />
          {pagination.total_pages > page && !fetchDataIsLoading && (
            <FlexBox justifyContent="center" UNSAFE_style={{ padding: '12px' }}>
              <Button kind="link" onClick={() => setPage(prevState => prevState + 1)}>
                Load more audiences
              </Button>
            </FlexBox>
          )}
        </>
      )}
      {fetchDataIsLoading && !audiences.length && <Loading />}
      {fetchDataIsLoading && !!audiences.length && <Loading style={{ minHeight: '0px' }} />}
    </div>
  );
};

export default AudienceTable;
